import React, { useEffect, useState, useCallback } from 'react';
import envConstants from "../Constants";
import axios from "../Axiosapi";
import { useLocation, useNavigate, Link } from "react-router-dom";
import ProductCard from '../Components/ProductCard';
import { Helmet } from "react-helmet-async";
import { useParams } from 'react-router-dom';

const Brand = () => {
    
    const [products, setProducts] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [previousPage, setPreviousPage] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [errorProducts, setErrorProducts] = useState(null);
    const [meta, setMeta] = useState();
    const [isReadMore, setIsReadMore] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    console.log("Complete URL:", window.location.href);
    const query = new URLSearchParams(location.search);
    const page = query.get('page') ? parseInt(query.get('page')) : 1;
    const brand = query.get('brand') || '';
    const brandTitle = brand ? `Brand: ${brand}` : 'All Brands';
    const fetchProducts = useCallback(async () => {
        setLoadingProducts(true);
        try {
            let url = `${envConstants.API_BASE_URL}/shop?page=${page}&brand=${brand}`;

            const response = await axios.get(url, {
                params: { pagination: 20 },
                responseType: 'json',
            });
            setMeta(response.data.meta);
            setProducts(response.data.data);
            setTotalCount(response.data.total);
            setTotalPages(response.data.last_page);
            setNextPage(page < response.data.last_page ? page + 1 : null);
            setPreviousPage(page > 1 ? page - 1 : null);
            setCurrentPage(page);
            setErrorProducts(null);
        } catch (error) {
            setErrorProducts(error);
            console.error(error);
        } finally {
            setLoadingProducts(false);
        }
    }, [brand, page]);

    // Fetch Products on Component Mount & Query Change
    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const buildLink = (pageNumber) => {
        return `/brand?page=${pageNumber}&brand=${brand}`;
    };

    // Render Pagination Numbers
    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        // First Page
        if (startPage > 1) {
            pageNumbers.push(
                <li key={1} className={`page-item ${currentPage === 1 ? 'active' : ''}`}>
                    <Link className="search-result-pag-btn" to={buildLink(1)}>1</Link>
                </li>
            );
            if (startPage > 2) {
                pageNumbers.push(
                    <li key="ellipsis-start" className="page-item disabled">
                        <span className="search-result-pag-btn">...</span>
                    </li>
                );
            }
        }

        // Middle Pages
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(
                <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
                    <Link className="search-result-pag-btn" to={buildLink(i)}>{i}</Link>
                </li>
            );
        }

        // Last Page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pageNumbers.push(
                    <li key="ellipsis-end" className="page-item disabled">
                        <span className="search-result-pag-btn">...</span>
                    </li>
                );
            }
            pageNumbers.push(
                <li key={totalPages} className={`page-item ${totalPages === currentPage ? 'active' : ''}`}>
                    <Link className="search-result-pag-btn" to={buildLink(totalPages)}>{totalPages}</Link>
                </li>
            );
        }

        return pageNumbers;
    };

    const canonicalUrl = window.location.href ? window.location.href || 'https://autopartstation.com.au/' : 'https://autopartstation.com.au/';
    const seotitle = meta ? meta.find(item => item.option === '_seo_title')?.value || '' : '';
    const seodescription = meta ? meta.find(item => item.option === '_seo_description')?.value || '' : '';
    const seotags = meta ? meta.find(item => item.option === '_seo_tags')?.value || '' : '';
    const schemaMarkup = meta ? meta.find(item => item.option === '_schema_markup')?.value || '' : ''
    const description = meta ? meta.find(item => item.option === '_description')?.value || '' : '';
    const image = meta ? meta.find(item => item.option === '_editor_image')?.value || '' : '';
    const index = meta ? meta.find(item => item.option === '_page_index')?.value : '1';

    const handleReadMore = () => {
        setIsReadMore(!isReadMore);
    };

    return (
        <>
            <Helmet>
                <title>{seotitle}</title>
                <meta name="description" content={seodescription} />
                <meta name="author" content={envConstants.STORE_META_AUTHOR ? envConstants.STORE_META_AUTHOR : 'Auto Part Station Store'} />
                <meta name="robots" content={index == 0 || index == '0' ? 'noindex, nofollow' : 'index, follow'} />
                <meta name="keywords" content={seotags} />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json">
                    {JSON.stringify(schemaMarkup)}
                </script>
            </Helmet>

            <div className="container my-5">
                <div className="sortbar flex-wrap align-items-center">
                    <h1 className='d-block m-0 mb-4'>
                        {brandTitle ? (
                            <span>{brandTitle}</span>
                        ) : (
                            'Brands'
                        )}
                    </h1>
                    <p className='d-block'>There are {totalCount} products.</p>
                </div>

                {/* Products Listing */}
                {loadingProducts ? (
                    <p className="text-center">Loading products...</p>
                ) : errorProducts ? (
                    <p className="text-center text-danger">Error loading products. Please try again later.</p>
                ) : products.length === 0 ? (
                    <p className='text-center'>No Product Found</p>
                ) : (
                    <div className="shop-product-list d-flex flex-wrap ps-md-1">
                        {products.slice(0, 15).map((product) => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {totalCount > 16 && (
                    <nav aria-label="Page navigation example">
                        <ul className="pagination mt-3 justify-content-md-end justify-content-center">
                            {renderPageNumbers()}
                        </ul>
                    </nav>
                )}
                {description && description.trim() !== '' && (
                    <div className="mt-5">
                        {/* Show truncated description initially */}
                        <div
                            dangerouslySetInnerHTML={{
                                __html: isReadMore ? description : `${description.slice(0, 7000)}...`,
                            }}
                        />
                        {/* Toggle between "Read More" and "Read Less" */}
                        <div className="d-flex justify-content-end">
                            <button
                                onClick={handleReadMore}
                                className="read-more"
                            >
                                {isReadMore ? 'Read Less' : 'Read More'}
                            </button>
                        </div>
                    </div>
                )}
                {image && image.trim() !== '' && (
                    <div>
                        <img
                            src={`${envConstants.API_BASE_PUBLIC_URL}${image}`}
                            alt="Brand Editor"
                            style={{ width: '100%', height: 'auto' }}
                        />
                    </div>
                )}
            </div>

        </>
    );
};

export default Brand;
